import { KeyValue } from "@angular/common";
import { DateTime } from "luxon";

export interface IBrandUrl {
    facebook?: string;
    instagram?: string;
    tiktok?: string;
    tripadvisor?: string;
    personal?: string;
}

export interface IOfferTag extends IOfferCard {
    tag_value_calculated: number;
    tiered?: boolean;
    upgrade?: boolean;
    savings: number;
}

export interface IBrandCard {
    _id: string;
    slug: string;
    name: string;
    short?: string;
    title?: string;
    color: string;
    logo_url: string;
    image_url: string;
    tags: IOfferTag[];
    options?: number[];
}

export interface IPlanGradient {
    color: string;
    stop: number;
}

export interface IPlan {
    _id: string;
    name: string;
    identifier: string;
    tags: string[];
    color_settings: {
        background: string;
        foreground: string;
        gradient_horizontal: IPlanGradient[];
        gradient_vertical: IPlanGradient[];
    }
}

export interface Brand extends IBrandCard {
    description: string;
    urls?: IBrandUrl;
    offers: Offer[];
    plan_details?: IPlan[];
    stores: Store[];
    categories: Category[];
}

export interface IOfferRestriction {
    days: number;
    holidays: boolean;
}

export interface IOfferTier extends Partial<Offer> {
    _id: string;
    redemptions_required: number;
    redemptions_given: number;
}

export interface IOfferTierTree {
    _id: string;
    version: number;
    deactivated?: string;
    terms: string[];
    tiers: IOfferTier[];
}

export interface IOfferLoyaltyProgress {
    _id: string;
    userredeemed: number;
    userlimit: number;
    target: number;
}

export interface IOfferLoyalty {
    _id: string;
    consumer: string;
    expiry: string;
    usable_on: string;
    progress: number;
    target: number;
    version: number;
    reward_progress: IOfferLoyaltyProgress[];
}

export interface IOfferCard {
    _id: string;
    short_title: string;
    url: string;
    plans: string[];
    plan: string;

    card_url?: string;
    external_url?: string;
    brand: IBrandCard;
    valid?: boolean;
    post_redeem?: string;
    prebook?: boolean;
    listed?: number;
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
    tag_type: string;
    tag_value: string;
    gallery_urls?: string[];

    redemption?: string;//redemption id
    redeemed?: number;
    userredeemed?: number;
    userlimit: number;
    userinterval: 'month' | 'year' | 'user';
    restrictions?: IOfferRestriction;
    people_min?: number;
    people_max?: number;
    saved: boolean;

    tier_tree?: IOfferTierTree;
    tiered_progress?: IOfferLoyalty;
    tier?: string;
}


export interface Offer extends IOfferCard {
    description: string;
    button_custom_text?: string;
    preuploaded?: boolean;
    external_url?: string;
    exclusive: boolean;
    terms: string[];
    online: boolean;
    stores?: Store[];
    listed?: number;
    codes?: number;//number of predefined codes
    categories: Category[];//category list
    keywords?: string[];
    preusable?: boolean;
    validation?: boolean;
    code_type?: number;
    redemption_type?: string;
    plan_details?: IPlan;
}

export interface IDay {
    day: number;
    open?: string;
    close?: string;
}

export interface IMenuInfo {
    _id: string;
    title: string;
    options: number;
    stores: (string | Store)[];
    type: 'Manual' | 'PDF' | 'Url';
}

export interface IMenu extends IMenuInfo {
    categories?: any[];
    menu_pdf?: string;
    menu_url?: string;
}

export interface Store {
    _id: string;
    name: string;
    phone_primary?: string;
    phone_secondary?: string;
    primary_cc?: string;
    secondary_cc?: string;
    primary_phone?: string;
    secondary_phone?: string;
    status?: boolean;
    location: {
        building: string;
        street: string;
        city: string;
        country: string;
        postcode: string;
        geo: {
            x: number;
            y: number;
        }
    };
    menus?: IMenuInfo[];
    days?: IDay[];
    hours: {
        monday: {
            open: string;
            close: string;
        };
        tuesday: {
            open: string;
            close: string;
        };
        wednesday: {
            open: string;
            close: string;
        };
        thursday: {
            open: string;
            close: string;
        };
        friday: {
            open: string;
            close: string;
        };
        saturday: {
            open: string;
            close: string;
        };
        sunday: {
            open: string;
            close: string;
        };
    };
    brand: string | Brand;
}

export interface Category {
    _id: string;
    name: string;
    slug: string;
    icon_svg: string;
    image_url: string;
}

export interface IAuthRequest {
    country_code: string;
    phone: string;
    token?: string;
}

export interface IAuthResponse {
    auth_id: string;
    code_length: number;
}

export interface IAuthResend {
    auth_id: string;
}

export interface IAuthVerify {
    auth_id: string;
    code: string;
}

export interface IAuthVerified {
    token: string;
    user?: string;
    [key: string]: any;
}

export interface IAuthRegister {
    auth_id: string;
    email: string;
    name: string;
    surname: string;
    birthday: string;
    gender: string;
    tourist: boolean;
    country?: string;
    city?: string;
    referral?: string;
}

export interface ISession {
    _id: string;
    plans: string[];
    view_plans: string[];
    package: string;
    package_name: string;
    expire: string;
    start: string;
    country: string;
    next_package: string | null;
    next_transaction: string | null;
    email_verified: boolean;
    requires_email_verified: boolean;
    phone_verified: boolean;
    created: string;
    colors?: {
        background: string;
        foreground: string;
        gradient_vertical: string;
        gradient_horizontal: string;
    }
}

export interface ICoupon {
    _id: string;
    code: string;
    reduction: number;
    percentage: boolean;
    package: string;
    unlimited: boolean;
    title: string;
    descriptor: string;
    whitelist: string[];
    age: number;
    expiry: Date;
    created: Date;
    bypass: boolean;
    free_days: number;
}

export interface INextAction {
    action: 'sync_start' | 'sync' | 'navigate' | 'confirm';
    path?: string;
    id?: string;
    data?: any;
}

export interface ICouponUse {
    _id: string;
    code: string;
    consumer?: string;
    priority: boolean;
    assigned?: string;
    coupon: ICoupon;
    used?: string;
    expiry?: string;
    created?: string;
    updated?: string;
    next_action?: INextAction
}

export interface IPackage {
    _id: string;
    name: string;
    short_name: string;
    slug: string;
    plans: string[];
    package_type: 'internal' | 'corporate';
    recurs?: boolean;
    price?: string;
    unit?: string;
    duration?: number;
    limited?: boolean;
    retailed?: boolean;
    prompt_start?: boolean;
    prompt_end?: boolean;
    extension?: { [key: string]: any };
    plan_details?: IPlan;
    tags?: string[];
}

export interface ITransaction {
    _id: string;
    interval: number;
    initial: boolean;
    level?: number;
    status: 'STARTED' | 'ACTION_REQUIRED' | 'PAID' | 'COMPLETED' | 'FAILED';
    consecutiveness?: number;
    package?: string;
    package_name?: string;
    package_slug?: string;
    base_price: string;
    vat?: number;
    calculated_vat?: string;
    final_price: string;
    consumer: string;
    start: string;
    end?: string;
    cycle_start?: string;
    source?: 'web' | 'app' | 'control' | 'automated';
    resultcode: string;
    payment_method?: {
        _id: string;
        setup_id: string;
        customer_id: string;
        card?: {
            brand: string;
            digits?: string;
            last4?: string;
            exp_month: number;
            exp_year: number;
        }
    };
    truevo?: { _id: string; details: any; };
    use?: {
        _id: string;
        code: string;
    };
    created: string;
    country?: string;
    error?: string;
    card?: any;
}

export interface ICampaign {
    _id: string;
    title?: string;
    type: string;
    trackable: string;
    reference?: string | any;
    gift_progression?: boolean;

    configuration?: any;
}

export interface ITerm {
    id: string;
    title: string;
    content: string;
    start: string;
    end: string;
    intro?: string;
}

export interface IGift {
    _id?: string;
    title?: string;
    logo_url?: string;
    brand?: IBrandCard;
    background_url?: string;
    started: boolean;
    ended: boolean;
    start: Date;
    end: Date;
    type: 'Bundle' | 'Voucher' | 'Coupon' | 'Credit';
    reservation: string;
    total: number;
    contents?: IGift[];
}

export interface IElement<T> {
    identifier: string;
    route_name: string;
    variables: T;
}

export interface UrlPreviewSeo {
    title: string;
    description: string;
    image: string;
}

export interface FinalWidgetPremiumPromoWeb {
    banner_desktop: string;
    banner_desktop_maltese?: string;
    banner_mobile: string;
    banner_mobile_maltese?: string;
    background_color: string;
    text_color: string;
    button_background_color: string;
    button_text_color: string;
    base_price: string;
    discounted_price?: string;
    duration: number;
    unit: string;
    code?: string;
    terms_fragment?: string;
    url_preview?: Partial<UrlPreviewSeo>;
};

export const humanize = (str: string) => {
    if (!str || typeof str != 'string') return null;
    return str.replace(/[_-]/gm, ' ').replace(/\w*/g, (txt) => { return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase(); });
}

export const cleanArray = (array: any[]) => array.filter(a => !!a);

export const weekdaysAsBitwise: KeyValue<string, number>[] = [
    { key: 'Monday', value: 1 },
    { key: 'Tuesday', value: 2 },
    { key: 'Wednesday', value: 4 },
    { key: 'Thursday', value: 8 },
    { key: 'Friday', value: 16 },
    { key: 'Saturday', value: 32 },
    { key: 'Sunday', value: 64 },
]

export const splitBitwise = (result: number) => {
    let bit = Math.pow(2, Math.ceil(Math.sqrt(result)));
    const options: number[] = [];

    while (bit >= 1) {
        if ((bit & result) == bit) {
            options.push(bit);
        }
        bit /= 2;
    }

    return options.reverse();
}

export const BitwiseToArray = (bitwise: number = 0, ascending = false) => {
    if (!bitwise) return [];

    const power = Math.floor(Math.log(bitwise) / Math.log(2));
    let currentBit = Math.pow(2, power);
    let options = [];

    while (currentBit > 0) {
        if (bitwise >= currentBit) {
            bitwise -= currentBit;
            options.push(currentBit);
        }
        currentBit /= 2;
    }

    return ascending ? options.reverse() : options;
};

export const calculatePriceWithCoupon = (pkg: IPackage, isPercentage: boolean, discount: number) => {
    if (!pkg) return '0.00';

    let price = Number(pkg.price || '0');

    if (isPercentage) {
        price = price * (1 - (discount / 100));
    } else {
        price -= discount;
    }

    price = Math.max(0, Math.min(price, Number(pkg.price)));

    return price.toFixed(2);
}

export const sortCouponsByValue = (pkg: IPackage, uses?: ICouponUse[]): ICouponUse[] => {
    if (!uses || !uses.length) return [];

    const filteredUses = uses.filter(u => u.coupon.package == pkg._id);

    const interval = DateTime.now().plus({ [pkg.unit ?? 'year']: pkg.duration ?? 1 }).diffNow('days').as('days');

    const calculatedUses = filteredUses.map(u => ({
        _id: u._id,
        price: calculatePriceWithCoupon(pkg, u.coupon.percentage, u.coupon.reduction),
        days: u.coupon && u.coupon.free_days || 0,
        assigned: u.assigned
    })).sort((a, b) => {

        const aPrice = Number(a.price);
        const bPrice = Number(b.price);
        const aDays = a.days || interval;
        const bDays = b.days || interval;

        const aCostPerDay = aPrice / aDays;
        const bCostPerDay = bPrice / bDays;

        if (aCostPerDay == bCostPerDay) {
            //more recent wins
            return DateTime.fromISO(a.assigned!) > (DateTime.fromISO(b.assigned!)) ? -1 : 1;
        }

        //more cost loses
        return aCostPerDay > bCostPerDay ? 1 : -1;

        // if(aPrice == bPrice){
        //   //more days wins
        //   return aDays > bDays ? -1 : 1;
        // }

        // //more price loses
        // return aPrice > bPrice ? 1 : -1;
    });

    return calculatedUses.map(u => uses.find(use => use._id == u._id)).filter(use => !!use);
}

export const menuOptions = [
    { key: 'Gluten-free', value: 1, icon: 'gluten_free' },
    { key: 'Lactose-free', value: 2, icon: 'lactose_free' },
    { key: 'Vegetarian', value: 4, icon: 'vegetarian' },
    { key: 'Vegan', value: 8, icon: 'vegan' },
];

export const cardTypes: string[] = [
    'amex',
    'diners',
    'discover',
    'elo',
    'jcb',
    'mastercard',
    'unionpay',
    'visa'
  ];

export const countries = [
    { "name": "Malta", "code": "mt", "dial_code": "+356", "english": "Malta", "position": "-3534px" },
    { "name": "افغانستان", "code": "af", "dial_code": "+93", "english": "Afghanistan", "position": "-66px" },
    { "name": "Åland", "code": "ax", "dial_code": "+358", "english": "Åland Islands", "position": "-330px" },
    { "name": "Shqipëria", "code": "al", "dial_code": "+355", "english": "Albania", "position": "-132px" },
    { "name": "الجزائر", "code": "dz", "dial_code": "+213", "english": "Algeria", "position": "-1423px" },
    { "name": "American Samoa", "code": "as", "dial_code": "+1684", "english": "American Samoa", "position": "-242px" },
    { "name": "Andorra", "code": "ad", "dial_code": "+376", "english": "Andorra", "position": "-22px" },
    { "name": "Angola", "code": "ao", "dial_code": "+244", "english": "Angola", "position": "-176px" },
    { "name": "Anguilla", "code": "ai", "dial_code": "+1264", "english": "Anguilla", "position": "-110px" },
    { "name": "Antarctica", "code": "aq", "dial_code": "+672", "english": "Antarctica", "position": "-198px" },
    { "name": "Antigua and Barbuda", "code": "ag", "dial_code": "+1268", "english": "Antigua and Barbuda", "position": "-88px" },
    { "name": "Argentina", "code": "ar", "dial_code": "+54", "english": "Argentina", "position": "-220px" },
    { "name": "Հայաստան", "code": "am", "dial_code": "+374", "english": "Armenia", "position": "-154px" },
    { "name": "Aruba", "code": "aw", "dial_code": "+297", "english": "Aruba", "position": "-308px" },
    { "name": "Australia", "code": "au", "dial_code": "+61", "english": "Australia", "position": "-286px" },
    { "name": "Österreich", "code": "at", "dial_code": "+43", "english": "Austria", "position": "-264px" },
    { "name": "Azərbaycan", "code": "az", "dial_code": "+994", "english": "Azerbaijan", "position": "-352px" },
    { "name": "Bahamas", "code": "bs", "dial_code": "+1242", "english": "Bahamas", "position": "-702px" },
    { "name": "‏البحرين", "code": "bh", "dial_code": "+973", "english": "Bahrain", "position": "-504px" },
    { "name": "Bangladesh", "code": "bd", "dial_code": "+880", "english": "Bangladesh", "position": "-418px" },
    { "name": "Barbados", "code": "bb", "dial_code": "+1246", "english": "Barbados", "position": "-396px" },
    { "name": "Белару́сь", "code": "by", "dial_code": "+375", "english": "Belarus", "position": "-790px" },
    { "name": "België", "code": "be", "dial_code": "+32", "english": "Belgium", "position": "-440px", "width": "18px" },
    { "name": "Belize", "code": "bz", "dial_code": "+501", "english": "Belize", "position": "-812px" },
    { "name": "Bénin", "code": "bj", "dial_code": "+229", "english": "Benin", "position": "-548px" },
    { "name": "Bermuda", "code": "bm", "dial_code": "+1441", "english": "Bermuda", "position": "-592px" },
    { "name": "ʼbrug-yul", "code": "bt", "dial_code": "+975", "english": "Bhutan", "position": "-724px" },
    { "name": "Bolivia", "code": "bo", "dial_code": "+591", "english": "Bolivia, Plurinational State of bolivia", "position": "-636px" },
    { "name": "Bosna i Hercegovina", "code": "ba", "dial_code": "+387", "english": "Bosnia and Herzegovina", "position": "-374px" },
    { "name": "Botswana", "code": "bw", "dial_code": "+267", "english": "Botswana", "position": "-768px" },
    { "name": "Bouvetøya", "code": "bv", "dial_code": "+47", "english": "Bouvet Island", "position": "-746px" },
    { "name": "Brasil", "code": "br", "dial_code": "+55", "english": "Brazil", "position": "-680px" },
    { "name": "British Indian Ocean Territory", "code": "io", "dial_code": "+246", "english": "British Indian Ocean Territory", "position": "-2501px" },
    { "name": "Negara Brunei Darussalam", "code": "bn", "dial_code": "+673", "english": "Brunei Darussalam", "position": "-614px" },
    { "name": "България", "code": "bg", "dial_code": "+359", "english": "Bulgaria", "position": "-482px" },
    { "name": "Burkina Faso", "code": "bf", "dial_code": "+226", "english": "Burkina Faso", "position": "-460px" },
    { "name": "Burundi", "code": "bi", "dial_code": "+257", "english": "Burundi", "position": "-526px" },
    { "name": "Kâmpŭchéa", "code": "kh", "dial_code": "+855", "english": "Cambodia", "position": "-2743px" },
    { "name": "Cameroon", "code": "cm", "dial_code": "+237", "english": "Cameroon", "position": "-1027px" },
    { "name": "Canada", "code": "ca", "dial_code": "+1", "english": "Canada", "position": "-834px" },
    { "name": "Cabo Verde", "code": "cv", "dial_code": "+238", "english": "Cape Verde", "position": "-1181px" },
    { "name": "Cayman Islands", "code": "ky", "dial_code": "+ 345", "english": "Cayman Islands", "position": "-2897px" },
    { "name": "Ködörösêse tî Bêafrîka", "code": "cf", "dial_code": "+236", "english": "Central African Republic", "position": "-900px" },
    { "name": "Tchad", "code": "td", "dial_code": "+235", "english": "Chad", "position": "-4933px" },
    { "name": "Chile", "code": "cl", "dial_code": "+56", "english": "Chile", "position": "-1005px" },
    { "name": "中国", "code": "cn", "dial_code": "+86", "english": "China", "position": "-1049px" },
    { "name": "Christmas Island", "code": "cx", "dial_code": "+61", "english": "Christmas Island", "position": "-1225px" },
    { "name": "Cocos (Keeling) Islands", "code": "cc", "dial_code": "+61", "english": "Cocos (Keeling) Islands", "position": "-856px" },
    { "name": "Colombia", "code": "co", "dial_code": "+57", "english": "Colombia", "position": "-1071px" },
    { "name": "Komori", "code": "km", "dial_code": "+269", "english": "Comoros", "position": "-2787px" },
    { "name": "République du Congo", "code": "cg", "dial_code": "+242", "english": "Congo", "position": "-922px" },
    { "name": "République démocratique du Congo", "code": "cd", "dial_code": "+243", "english": "Congo, The Democratic Republic of the Congo", "position": "-878px" },
    { "name": "Cook Islands", "code": "ck", "dial_code": "+682", "english": "Cook Islands", "position": "-983px" },
    { "name": "Costa Rica", "code": "cr", "dial_code": "+506", "english": "Costa Rica", "position": "-1137px" },
    { "name": "Côte d'Ivoire", "code": "ci", "dial_code": "+225", "english": "Cote d'Ivoire", "position": "-961px" },
    { "name": "Hrvatska", "code": "hr", "dial_code": "+385", "english": "Croatia", "position": "-2303px" },
    { "name": "Cuba", "code": "cu", "dial_code": "+53", "english": "Cuba", "position": "-1159px" },
    { "name": "Κύπρος", "code": "cy", "dial_code": "+357", "english": "Cyprus", "position": "-1247px" },
    { "name": "Česká republika", "code": "cz", "dial_code": "+420", "english": "Czech Republic", "position": "-1269px" },
    { "name": "Danmark", "code": "dk", "dial_code": "+45", "english": "Denmark", "position": "-1357px" },
    { "name": "Djibouti", "code": "dj", "dial_code": "+253", "english": "Djibouti", "position": "-1335px" },
    { "name": "Dominica", "code": "dm", "dial_code": "+1767", "english": "Dominica", "position": "-1379px" },
    { "name": "República Dominicana", "code": "do", "dial_code": "+1849", "english": "Dominican Republic", "position": "-1401px" },
    { "name": "Ecuador", "code": "ec", "dial_code": "+593", "english": "Ecuador", "position": "-1467px" },
    { "name": "مصر‎", "code": "eg", "dial_code": "+20", "english": "Egypt", "position": "-1511px" },
    { "name": "El Salvador", "code": "sv", "dial_code": "+503", "english": "El Salvador", "position": "-4801px" },
    { "name": "Guinea Ecuatorial", "code": "gq", "dial_code": "+240", "english": "Equatorial Guinea", "position": "-2083px" },
    { "name": "ኤርትራ", "code": "er", "dial_code": "+291", "english": "Eritrea", "position": "-1555px" },
    { "name": "Eesti", "code": "ee", "dial_code": "+372", "english": "Estonia", "position": "-1489px" },
    { "name": "ኢትዮጵያ", "code": "et", "dial_code": "+251", "english": "Ethiopia", "position": "-1599px" },
    { "name": "Falkland Islands", "code": "fk", "dial_code": "+500", "english": "Falkland Islands (Malvinas)", "position": "-1709px" },
    { "name": "Føroyar", "code": "fo", "dial_code": "+298", "english": "Faroe Islands", "position": "-1753px" },
    { "name": "Fiji", "code": "fj", "dial_code": "+679", "english": "Fiji", "position": "-1687px" },
    { "name": "Suomi", "code": "fi", "dial_code": "+358", "english": "Finland", "position": "-1665px" },
    { "name": "France", "code": "fr", "dial_code": "+33", "english": "France", "position": "-1775px" },
    { "name": "Guyane française", "code": "gf", "dial_code": "+594", "english": "French Guiana", "position": "-1907px" },
    { "name": "Polynésie française", "code": "pf", "dial_code": "+689", "english": "French Polynesia", "position": "-4009px" },
    { "name": "Territoire des Terres australes et antarctiques fr", "code": "tf", "dial_code": "+262", "english": "French Southern Territories", "position": "-4955px" },
    { "name": "Gabon", "code": "ga", "dial_code": "+241", "english": "Gabon", "position": "-1819px" },
    { "name": "Gambia", "code": "gm", "dial_code": "+220", "english": "Gambia", "position": "-2017px" },
    { "name": "საქართველო", "code": "ge", "dial_code": "+995", "english": "Georgia", "position": "-1885px" },
    { "name": "Deutschland", "code": "de", "dial_code": "+49", "english": "Germany", "position": "-1291px" },
    { "name": "Ghana", "code": "gh", "dial_code": "+233", "english": "Ghana", "position": "-1951px" },
    { "name": "Gibraltar", "code": "gi", "dial_code": "+350", "english": "Gibraltar", "position": "-1973px" },
    { "name": "Ελλάδα", "code": "gr", "dial_code": "+30", "english": "Greece", "position": "-2105px" },
    { "name": "Kalaallit Nunaat", "code": "gl", "dial_code": "+299", "english": "Greenland", "position": "-1995px" },
    { "name": "Grenada", "code": "gd", "dial_code": "+1473", "english": "Grenada", "position": "-1863px" },
    { "name": "Guadeloupe", "code": "gp", "dial_code": "+590", "english": "Guadeloupe", "position": "-2061px" },
    { "name": "Guam", "code": "gu", "dial_code": "+1671", "english": "Guam", "position": "-2171px" },
    { "name": "Guatemala", "code": "gt", "dial_code": "+502", "english": "Guatemala", "position": "-2149px" },
    { "name": "Guernsey", "code": "gg", "dial_code": "+44", "english": "Guernsey", "position": "-1929px" },
    { "name": "Guinée", "code": "gn", "dial_code": "+224", "english": "Guinea", "position": "-2039px" },
    { "name": "Guiné-Bissau", "code": "gw", "dial_code": "+245", "english": "Guinea-Bissau", "position": "-2193px" },
    { "name": "Guyana", "code": "gy", "dial_code": "+592", "english": "Guyana", "position": "-2215px" },
    { "name": "Haïti", "code": "ht", "dial_code": "+509", "english": "Haiti", "position": "-2325px" },
    { "name": "Heard Island and McDonald Islands", "code": "hm", "dial_code": "+0", "english": "Heard Island and Mcdonald Islands", "position": "-2259px" },
    { "name": "Vaticano", "code": "va", "dial_code": "+379", "english": "Holy See (Vatican City State)", "position": "-5439px", "width": "15px" },
    { "name": "Honduras", "code": "hn", "dial_code": "+504", "english": "Honduras", "position": "-2281px" },
    { "name": "香港", "code": "hk", "dial_code": "+852", "english": "Hong Kong", "position": "-2237px" },
    { "name": "Magyarország", "code": "hu", "dial_code": "+36", "english": "Hungary", "position": "-2347px" },
    { "name": "Ísland", "code": "is", "dial_code": "+354", "english": "Iceland", "position": "-2567px" },
    { "name": "भारत", "code": "in", "dial_code": "+91", "english": "India", "position": "-2479px" },
    { "name": "Indonesia", "code": "id", "dial_code": "+62", "english": "Indonesia", "position": "-2391px" },
    { "name": "ایران", "code": "ir", "dial_code": "+98", "english": "Iran, Islamic Republic of Persian Gulf", "position": "-2545px" },
    { "name": "العراق", "code": "iq", "dial_code": "+964", "english": "Iraq", "position": "-2523px" },
    { "name": "Éire", "code": "ie", "dial_code": "+353", "english": "Ireland", "position": "-2413px" },
    { "name": "Isle of Man", "code": "im", "dial_code": "+44", "english": "Isle of Man", "position": "-2457px" },
    { "name": "יִשְׂרָאֵל", "code": "il", "dial_code": "+972", "english": "Israel", "position": "-2435px" },
    { "name": "Italia", "code": "it", "dial_code": "+39", "english": "Italy", "position": "-2589px" },
    { "name": "Jamaica", "code": "jm", "dial_code": "+1876", "english": "Jamaica", "position": "-2633px" },
    { "name": "日本", "code": "jp", "dial_code": "+81", "english": "Japan", "position": "-2677px" },
    { "name": "Jersey", "code": "je", "dial_code": "+44", "english": "Jersey", "position": "-2611px" },
    { "name": "الأردن", "code": "jo", "dial_code": "+962", "english": "Jordan", "position": "-2655px" },
    { "name": "Қазақстан", "code": "kz", "dial_code": "+7", "english": "Kazakhstan", "position": "-2919px" },
    { "name": "Kenya", "code": "ke", "dial_code": "+254", "english": "Kenya", "position": "-2699px" },
    { "name": "Kiribati", "code": "ki", "dial_code": "+686", "english": "Kiribati", "position": "-2765px" },
    { "name": "북한", "code": "kp", "dial_code": "+850", "english": "Korea, Democratic People's Republic of Korea", "position": "-2831px" },
    { "name": "대한민국", "code": "kr", "dial_code": "+82", "english": "Korea, Republic of South Korea", "position": "-2853px" },
    { "name": "Republika e Kosovës", "code": "xk", "dial_code": "+383", "english": "Kosovo", "position": "-5632px" },
    { "name": "الكويت", "code": "kw", "dial_code": "+965", "english": "Kuwait", "position": "-2875px" },
    { "name": "Kyrgyzstan", "code": "kg", "dial_code": "+996", "english": "Kyrgyzstan", "position": "-2721px" },
    { "name": "ສປປລາວ", "code": "la", "dial_code": "+856", "english": "Laos", "position": "-2941px" },
    { "name": "Latvija", "code": "lv", "dial_code": "+371", "english": "Latvia", "position": "-3139px" },
    { "name": "لبنان", "code": "lb", "dial_code": "+961", "english": "Lebanon", "position": "-2963px" },
    { "name": "Lesotho", "code": "ls", "dial_code": "+266", "english": "Lesotho", "position": "-3073px" },
    { "name": "Liberia", "code": "lr", "dial_code": "+231", "english": "Liberia", "position": "-3051px" },
    { "name": "‏ليبيا", "code": "ly", "dial_code": "+218", "english": "Libyan Arab Jamahiriya", "position": "-3161px" },
    { "name": "Liechtenstein", "code": "li", "dial_code": "+423", "english": "Liechtenstein", "position": "-3007px" },
    { "name": "Lietuva", "code": "lt", "dial_code": "+370", "english": "Lithuania", "position": "-3095px" },
    { "name": "Luxembourg", "code": "lu", "dial_code": "+352", "english": "Luxembourg", "position": "-3117px" },
    { "name": "澳門", "code": "mo", "dial_code": "+853", "english": "Macao", "position": "-3424px" },
    { "name": "Македонија", "code": "mk", "dial_code": "+389", "english": "Macedonia", "position": "-3336px" },
    { "name": "Madagasikara", "code": "mg", "dial_code": "+261", "english": "Madagascar", "position": "-3292px" },
    { "name": "Malawi", "code": "mw", "dial_code": "+265", "english": "Malawi", "position": "-3600px" },
    { "name": "Malaysia", "code": "my", "dial_code": "+60", "english": "Malaysia", "position": "-3644px" },
    { "name": "Maldives", "code": "mv", "dial_code": "+960", "english": "Maldives", "position": "-3578px" },
    { "name": "Mali", "code": "ml", "dial_code": "+223", "english": "Mali", "position": "-3358px" },
    { "name": "M̧ajeļ", "code": "mh", "dial_code": "+692", "english": "Marshall Islandds", "position": "-3314px" },
    { "name": "Martinique", "code": "mq", "dial_code": "+596", "english": "Martinique", "position": "-3468px" },
    { "name": "موريتانيا", "code": "mr", "dial_code": "+222", "english": "Mauritania", "position": "-3490px" },
    { "name": "Maurice", "code": "mu", "dial_code": "+230", "english": "Mauritius", "position": "-3556px" },
    { "name": "Mayotte", "code": "yt", "dial_code": "+262", "english": "Mayotte", "position": "-5676px" },
    { "name": "México", "code": "mx", "dial_code": "+52", "english": "Mexico", "position": "-3622px" },
    { "name": "Micronesia", "code": "fm", "dial_code": "+691", "english": "Micronesia, Federated States of Micronesia", "position": "-1731px" },
    { "name": "Moldova", "code": "md", "dial_code": "+373", "english": "Moldova", "position": "-3226px" },
    { "name": "Monaco", "code": "mc", "dial_code": "+377", "english": "Monaco", "position": "-3205px", "width": "19px" },
    { "name": "Монгол улс", "code": "mn", "dial_code": "+976", "english": "Mongolia", "position": "-3402px" },
    { "name": "Црна Гора", "code": "me", "dial_code": "+382", "english": "Montenegro", "position": "-3248px" },
    { "name": "Montserrat", "code": "ms", "dial_code": "+1664", "english": "Montserrat", "position": "-3512px" },
    { "name": "المغرب", "code": "ma", "dial_code": "+212", "english": "Morocco", "position": "-3183px" },
    { "name": "Moçambique", "code": "mz", "dial_code": "+258", "english": "Mozambique", "position": "-3666px" },
    { "name": "Myanma", "code": "mm", "dial_code": "+95", "english": "Myanmar", "position": "-3380px" },
    { "name": "Namibia", "code": "na", "dial_code": "+264", "english": "Namibia", "position": "-3688px" },
    { "name": "Nauru", "code": "nr", "dial_code": "+674", "english": "Nauru", "position": "-3877px" },
    { "name": "नपल", "code": "np", "dial_code": "+977", "english": "Nepal", "position": "-3862px", "width": "13px" },
    { "name": "Nederland", "code": "nl", "dial_code": "+31", "english": "Netherlands", "position": "-3818px" },
    { "name": "Netherlands Antilles", "code": "an", "dial_code": "+599", "english": "Netherlands Antilles", "position": "-3818px" },
    { "name": "Nouvelle-Calédonie", "code": "nc", "dial_code": "+687", "english": "New Caledonia", "position": "-3710px" },
    { "name": "New Zealand", "code": "nz", "dial_code": "+64", "english": "New Zealand", "position": "-3921px" },
    { "name": "Nicaragua", "code": "ni", "dial_code": "+505", "english": "Nicaragua", "position": "-3796px" },
    { "name": "Niger", "code": "ne", "dial_code": "+227", "english": "Niger", "position": "-3732px", "width": "18px" },
    { "name": "Nigeria", "code": "ng", "dial_code": "+234", "english": "Nigeria", "position": "-3774px" },
    { "name": "Niuē", "code": "nu", "dial_code": "+683", "english": "Niue", "position": "-3899px" },
    { "name": "Norfolk Island", "code": "nf", "dial_code": "+672", "english": "Norfolk Island", "position": "-3752px" },
    { "name": "Northern Mariana Islands", "code": "mp", "dial_code": "+1670", "english": "Northern Mariana Islands", "position": "-3446px" },
    { "name": "Norge", "code": "no", "dial_code": "+47", "english": "Norway", "position": "-3840px" },
    { "name": "عمان", "code": "om", "dial_code": "+968", "english": "Oman", "position": "-3943px" },
    { "name": "Pakistan", "code": "pk", "dial_code": "+92", "english": "Pakistan", "position": "-4075px" },
    { "name": "Palau", "code": "pw", "dial_code": "+680", "english": "Palau", "position": "-4229px" },
    { "name": "فلسطين", "code": "ps", "dial_code": "+970", "english": "Palestinian Territory, Occupied", "position": "-4185px" },
    { "name": "Panamá", "code": "pa", "dial_code": "+507", "english": "Panama", "position": "-3965px" },
    { "name": "Papua Niugini", "code": "pg", "dial_code": "+675", "english": "Papua New Guinea", "position": "-4031px" },
    { "name": "Paraguay", "code": "py", "dial_code": "+595", "english": "Paraguay", "position": "-4251px" },
    { "name": "Perú", "code": "pe", "dial_code": "+51", "english": "Peru", "position": "-3987px" },
    { "name": "Pilipinas", "code": "ph", "dial_code": "+63", "english": "Philippines", "position": "-4053px" },
    { "name": "Pitcairn Islands", "code": "pn", "dial_code": "+64", "english": "Pitcairn", "position": "-4141px" },
    { "name": "Polska", "code": "pl", "dial_code": "+48", "english": "Poland", "position": "-4097px" },
    { "name": "Portugal", "code": "pt", "dial_code": "+351", "english": "Portugal", "position": "-4207px" },
    { "name": "Puerto Rico", "code": "pr", "dial_code": "+1939", "english": "Puerto Rico", "position": "-4163px" },
    { "name": "قطر", "code": "qa", "dial_code": "+974", "english": "Qatar", "position": "-4273px" },
    { "name": "România", "code": "ro", "dial_code": "+40", "english": "Romania", "position": "-4317px" },
    { "name": "Россия", "code": "ru", "dial_code": "+7", "english": "Russia", "position": "-4361px" },
    { "name": "Rwanda", "code": "rw", "dial_code": "+250", "english": "Rwanda", "position": "-4383px" },
    { "name": "La Réunion", "code": "re", "dial_code": "+262", "english": "Reunion", "position": "-4295px" },
    { "name": "Saint-Barthélemy", "code": "bl", "dial_code": "+590", "english": "Saint Barthelemy", "position": "-570px" },
    { "name": "Saint Helena", "code": "sh", "dial_code": "+290", "english": "Saint Helena, Ascension and Tristan Da Cunha", "position": "-4537px" },
    { "name": "Saint Kitts and Nevis", "code": "kn", "dial_code": "+1869", "english": "Saint Kitts and Nevis", "position": "-2809px" },
    { "name": "Saint Lucia", "code": "lc", "dial_code": "+1758", "english": "Saint Lucia", "position": "-2985px" },
    { "name": "Saint-Martin", "code": "mf", "dial_code": "+590", "english": "Saint Martin", "position": "-3270px" },
    { "name": "Saint-Pierre-et-Miquelon", "code": "pm", "dial_code": "+508", "english": "Saint Pierre and Miquelon", "position": "-4119px" },
    { "name": "Saint Vincent and the Grenadines", "code": "vc", "dial_code": "+1784", "english": "Saint Vincent and the Grenadines", "position": "-5456px" },
    { "name": "Samoa", "code": "ws", "dial_code": "+685", "english": "Samoa", "position": "-5610px" },
    { "name": "San Marino", "code": "sm", "dial_code": "+378", "english": "San Marino", "position": "-4647px" },
    { "name": "São Tomé e Príncipe", "code": "st", "dial_code": "+239", "english": "Sao Tome and Principe", "position": "-4757px" },
    { "name": "العربية السعودية", "code": "sa", "dial_code": "+966", "english": "Saudi Arabia", "position": "-4405px" },
    { "name": "Sénégal", "code": "sn", "dial_code": "+221", "english": "Senegal", "position": "-4669px" },
    { "name": "Србија", "code": "rs", "dial_code": "+381", "english": "Serbia", "position": "-4339px" },
    { "name": "Seychelles", "code": "sc", "dial_code": "+248", "english": "Seychelles", "position": "-4449px" },
    { "name": "Sierra Leone", "code": "sl", "dial_code": "+232", "english": "Sierra Leone", "position": "-4625px" },
    { "name": "Singapore", "code": "sg", "dial_code": "+65", "english": "Singapore", "position": "-4515px" },
    { "name": "Slovensko", "code": "sk", "dial_code": "+421", "english": "Slovakia", "position": "-4603px" },
    { "name": "Slovenija", "code": "si", "dial_code": "+386", "english": "Slovenia", "position": "-4559px" },
    { "name": "Solomon Islands", "code": "sb", "dial_code": "+677", "english": "Solomon Islands", "position": "-4427px" },
    { "name": "Soomaaliya", "code": "so", "dial_code": "+252", "english": "Somalia", "position": "-4691px" },
    { "name": "South Africa", "code": "za", "dial_code": "+27", "english": "South Africa", "position": "-5698px" },
    { "name": "South Sudan", "code": "ss", "dial_code": "+211", "english": "South Sudan", "position": "-4735px" },
    { "name": "South Georgia", "code": "gs", "dial_code": "+500", "english": "South Georgia and the South Sandwich Islands", "position": "-2127px" },
    { "name": "España", "code": "es", "dial_code": "+34", "english": "Spain", "position": "-1577px" },
    { "name": "śrī laṃkāva", "code": "lk", "dial_code": "+94", "english": "Sri Lanka", "position": "-3029px" },
    { "name": "السودان", "code": "sd", "dial_code": "+249", "english": "Sudan", "position": "-4471px" },
    { "name": "Suriname", "code": "sr", "dial_code": "+597", "english": "Suriname", "position": "-4713px" },
    { "name": "Svalbard og Jan Mayen", "code": "sj", "dial_code": "+47", "english": "Svalbard and Jan Mayen", "position": "-4581px" },
    { "name": "Swaziland", "code": "sz", "dial_code": "+268", "english": "Swaziland", "position": "-4867px" },
    { "name": "Sverige", "code": "se", "dial_code": "+46", "english": "Sweden", "position": "-4493px" },
    { "name": "Schweiz", "code": "ch", "dial_code": "+41", "english": "Switzerland", "position": "-944px", "width": "15px" },
    { "name": "سوريا", "code": "sy", "dial_code": "+963", "english": "Syrian Arab Republic", "position": "-4845px" },
    { "name": "臺灣", "code": "tw", "dial_code": "+886", "english": "Taiwan", "position": "-5219px" },
    { "name": "Тоҷикистон", "code": "tj", "dial_code": "+992", "english": "Tajikistan", "position": "-5021px" },
    { "name": "Tanzania", "code": "tz", "dial_code": "+255", "english": "Tanzania, United Republic of Tanzania", "position": "-5241px" },
    { "name": "ประเทศไทย", "code": "th", "dial_code": "+66", "english": "Thailand", "position": "-4999px" },
    { "name": "Timor-Leste", "code": "tl", "dial_code": "+670", "english": "Timor-Leste", "position": "-5065px" },
    { "name": "Togo", "code": "tg", "dial_code": "+228", "english": "Togo", "position": "-4977px" },
    { "name": "Tokelau", "code": "tk", "dial_code": "+690", "english": "Tokelau", "position": "-5043px" },
    { "name": "Tonga", "code": "to", "dial_code": "+676", "english": "Tonga", "position": "-5131px" },
    { "name": "Trinidad and Tobago", "code": "tt", "dial_code": "+1868", "english": "Trinidad and Tobago", "position": "-5175px" },
    { "name": "تونس", "code": "tn", "dial_code": "+216", "english": "Tunisia", "position": "-5109px" },
    { "name": "Türkiye", "code": "tr", "dial_code": "+90", "english": "Turkey", "position": "-5153px" },
    { "name": "Türkmenistan", "code": "tm", "dial_code": "+993", "english": "Turkmenistan", "position": "-5087px" },
    { "name": "Turks and Caicos Islands", "code": "tc", "dial_code": "+1649", "english": "Turks and Caicos Islands", "position": "-4911px" },
    { "name": "Tuvalu", "code": "tv", "dial_code": "+688", "english": "Tuvalu", "position": "-5197px" },
    { "name": "Uganda", "code": "ug", "dial_code": "+256", "english": "Uganda", "position": "-5285px" },
    { "name": "Україна", "code": "ua", "dial_code": "+380", "english": "Ukraine", "position": "-5263px" },
    { "name": "دولة الإمارات العربية المتحدة", "code": "ae", "dial_code": "+971", "english": "United Arab Emirates", "position": "-44px" },
    { "name": "United Kingdom", "code": "gb", "dial_code": "+44", "english": "United Kingdom", "position": "-1841px" },
    { "name": "United States", "code": "us", "dial_code": "+1", "english": "United States", "position": "-5373px" },
    { "name": "Uruguay", "code": "uy", "dial_code": "+598", "english": "Uruguay", "position": "-5395px" },
    { "name": "O‘zbekiston", "code": "uz", "dial_code": "+998", "english": "Uzbekistan", "position": "-5417px" },
    { "name": "Vanuatu", "code": "vu", "dial_code": "+678", "english": "Vanuatu", "position": "-5566px" },
    { "name": "Venezuela", "code": "ve", "dial_code": "+58", "english": "Venezuela, Bolivarian Republic of Venezuela", "position": "-5478px" },
    { "name": "Việt Nam", "code": "vn", "dial_code": "+84", "english": "Vietnam", "position": "-5544px" },
    { "name": "British Virgin Islands", "code": "vg", "dial_code": "+1284", "english": "Virgin Islands, British", "position": "-5500px" },
    { "name": "United States Virgin Islands", "code": "vi", "dial_code": "+1340", "english": "Virgin Islands, U.S.", "position": "-5522px" },
    { "name": "Wallis et Futuna", "code": "wf", "dial_code": "+681", "english": "Wallis and Futuna", "position": "-5588px" },
    { "name": "اليَمَن", "code": "ye", "dial_code": "+967", "english": "Yemen", "position": "-5654px" },
    { "name": "Zambia", "code": "zm", "dial_code": "+260", "english": "Zambia", "position": "-5720px" },
    { "name": "Zimbabwe", "code": "zw", "dial_code": "+263", "english": "Zimbabwe", "position": "-5742px" },
];

export const cities = [
    { name: "Attard", country: "Malta" },
    { name: "Balzan", country: "Malta" },
    { name: "Birgu", country: "Malta" },
    { name: "Birkirkara", country: "Malta" },
    { name: "Birzebbugia", country: "Malta" },
    { name: "Bormla", country: "Malta" },
    { name: "Bugibba", country: "Malta" },
    { name: "Burmarrad", country: "Malta" },
    { name: "Buskett", country: "Malta" },
    { name: "Cirkewwa", country: "Malta" },
    { name: "Dingli", country: "Malta" },
    { name: "Hal Far", country: "Malta" },
    { name: "Fontana", country: "Gozo" },
    { name: "Fgura", country: "Malta" },
    { name: "Furjana", country: "Malta" },
    { name: "Ghajnsielem", country: "Gozo" },
    { name: "Gharb", country: "Gozo" },
    { name: "Gharghur", country: "Malta" },
    { name: "Ghasri", country: "Gozo" },
    { name: "Ghaxaq", country: "Malta" },
    { name: "Gudja", country: "Malta" },
    { name: "Gzira", country: "Malta" },
    { name: "Hamrun", country: "Malta" },
    { name: "Iklin", country: "Malta" },
    { name: "Isla", country: "Malta" },
    { name: "Kalkara", country: "Malta" },
    { name: "Kappara", country: "Malta" },
    { name: "Kercem", country: "Gozo" },
    { name: "Kirkop", country: "Malta" },
    { name: "Lija", country: "Malta" },
    { name: "Luqa", country: "Malta" },
    { name: "Marsa", country: "Malta" },
    { name: "Marsalforn", country: "Gozo" },
    { name: "Marsaskala", country: "Malta" },
    { name: "Marsaxlokk", country: "Malta" },
    { name: "Mdina", country: "Malta" },
    { name: "Mellieha", country: "Malta" },
    { name: "Mgarr", country: "Malta" },
    { name: "Mosta", country: "Malta" },
    { name: "Mqabba", country: "Malta" },
    { name: "Msida", country: "Malta" },
    { name: "Mtarfa", country: "Malta" },
    { name: "Munxar", country: "Gozo" },
    { name: "Nadur", country: "Gozo" },
    { name: "Naxxar", country: "Malta" },
    { name: "Paola", country: "Malta" },
    { name: "Pembroke", country: "Malta" },
    { name: "Pieta", country: "Malta" },
    { name: "Ta' Qali", country: "Malta" },
    { name: "Qawra", country: "Malta" },
    { name: "Qormi", country: "Malta" },
    { name: "Qrendi", country: "Malta" },
    { name: "Rabat", actual: 'Rabat (Malta)', country: "Malta" },
    { name: "Rabat", actual: 'Rabat (Ghawdex)', country: "Gozo" },
    { name: "Safi", country: "Malta" },
    { name: "San Gwann", country: "Malta" },
    { name: "San Lawrenz", country: "Gozo" },
    { name: "Sannat", country: "Gozo" },
    { name: "Santa Lucija", country: "Malta" },
    { name: "Siggiewi", country: "Malta" },
    { name: "Sliema", country: "Malta" },
    { name: "St. Julian's", country: "Malta" },
    { name: "St. Paul's Bay", country: "Malta" },
    { name: "St. Venera", country: "Malta" },
    { name: "Swatar", country: "Malta" },
    { name: "Swieqi", country: "Malta" },
    { name: "Tarxien", country: "Malta" },
    { name: "Ta' Xbiex", country: "Malta" },
    { name: "Valletta", country: "Malta" },
    { name: "Xaghra", country: "Gozo" },
    { name: "Xewkija", country: "Gozo" },
    { name: "Xghajra", country: "Malta" },
    { name: "Xlendi", country: "Gozo" },
    { name: "Zabbar", country: "Malta" },
    { name: "Zebbug", actual: 'Zebbug (Malta)', country: "Malta" },
    { name: "Zebbug", actual: 'Zebbug (Ghawdex)', country: "Gozo" },
    { name: "Zejtun", country: "Malta" },
    { name: "Zurrieq", country: "Malta" }
];

